/**
 * AdminOverviewPage — `/admin` landing dashboard.
 *
 * Per `.design-incoming/admin-20260522/OVERVIEW_SPEC.md` + the
 * "Overview — light" design artboard. SCRUM-26. The landing
 * screen for any signed-in admin user.
 *
 * ## Layout (matches the design artboard)
 *
 *   - Eyebrow "INTERNAL ADMIN" + time-based greeting
 *     ("Good morning, {first name}") + subtitle ("{weekday},
 *     {month day}"; staff-online / imports-running counts append
 *     only once real metrics exist — never as faked zeros).
 *   - 4 stat cards (Active ISAs / Active MSAs / In testing /
 *     Closed (90d)) — value + delta caption.
 *   - Two-column band: Recent companies (left, wider) + Active
 *     imports (right, narrower).
 *   - Activity feed (full width below) — latest audit entries.
 *   - Mobile: stat cards 2-up, columns stack.
 *
 * ## Phase-1 data — NO dummy data
 *
 * `useAdminOverview()` returns an EMPTY dataset: there is no
 * backend yet, and fabricated metrics/feeds misrepresent the
 * system's real state. The four stat cards show "—"; every feed
 * renders its honest empty state ("No companies yet", "No imports
 * in flight.", "No recent activity."). When the BE ships
 * `GET /admin/metrics/companies` + the recent-companies / imports /
 * activity endpoints, swap `EMPTY_OVERVIEW` for the per-section
 * TanStack queries (OVERVIEW_SPEC.md §8 independent loading). Tests
 * drive the populated layout through the `dataOverride` prop.
 */

import * as React from 'react';
import { Link } from 'react-router-dom';
import { Button, Card } from '@sparta/ui-kit';
import { useMe } from '../../../shared/auth/use-me.js';
import { TypeChip, type CompanyType } from '../_components/TypeChip.js';
import {
  SystemStatusBadge,
  type SystemStatus,
} from '../_components/SystemStatusBadge.js';
import { ImportStatusDot } from '../_components/ImportStatusDot.js';
import { useCompactMode } from '../../../shared/hooks/useCompactMode.js';
import {
  importTypeLabel,
  type ImportType,
} from '../_components/ImportTypeChip.js';

// ── Greeting helpers (pure, exported for tests) ────────────────

export function greetingForHour(hour: number): string {
  if (hour >= 4 && hour < 12) return 'Good morning';
  if (hour >= 12 && hour < 18) return 'Good afternoon';
  return 'Good evening';
}

/**
 * First name for the greeting. Prefers `me.user.name`; falls back
 * to the local part of the email (title-cased); finally "there".
 */
export function greetingNameOf(
  name: string | undefined,
  email: string | undefined,
): string {
  const trimmed = name?.trim();
  if (trimmed !== undefined && trimmed.length > 0) {
    return trimmed.split(/\s+/)[0] ?? trimmed;
  }
  if (email !== undefined && email.includes('@')) {
    const local = email.slice(0, email.indexOf('@'));
    if (local.length > 0) {
      return local.charAt(0).toUpperCase() + local.slice(1);
    }
  }
  return 'there';
}

const WEEKDAYS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
] as const;
const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const;

export function formatGreetingDate(d: Date): string {
  const wd = WEEKDAYS[d.getDay()] ?? '';
  const mo = MONTHS[d.getMonth()] ?? '';
  return `${wd}, ${mo} ${d.getDate()}`;
}

// ── Data shapes ────────────────────────────────────────────────

interface OverviewStat {
  readonly label: string;
  /** Number once metrics ship; "—" in the pre-backend empty state. */
  readonly value: number | string;
  /** Delta / context caption under the value. Empty until metrics ship. */
  readonly caption: string;
}

interface OverviewCompany {
  readonly id: string;
  readonly type: CompanyType;
  readonly name: string;
  readonly status: SystemStatus;
  /** Display region label (e.g. "US-West") — free string per artboard. */
  readonly region: string;
  /** Property count; null renders as "—" (e.g. testing ISAs). */
  readonly propsCount: number | null;
}

interface OverviewImport {
  readonly id: string;
  readonly company_name: string;
  readonly type: ImportType;
  readonly status:
    | 'queued'
    | 'running'
    | 'completed'
    | 'completed_with_errors'
    | 'failed';
  readonly rows_total: number;
  readonly rows_processed: number;
  /** Already-formatted relative time per artboard ("12m ago", "Yesterday"). */
  readonly when: string;
}

interface OverviewActivity {
  readonly id: string;
  readonly actor_name: string;
  readonly verb: string;
  readonly target: string;
  readonly when: string;
}

export interface AdminOverviewData {
  readonly stats: readonly OverviewStat[];
  readonly staffOnline: number;
  readonly importsRunning: number;
  readonly recentCompanies: readonly OverviewCompany[];
  readonly activeImports: readonly OverviewImport[];
  readonly importsInQueue: number;
  readonly activity: readonly OverviewActivity[];
}

// ── Phase-1 empty state (no dummy data) ────────────────────────
//
// The admin Overview ships with NO fabricated metrics or feeds —
// there is no backend yet, and faked numbers/rows misrepresent the
// system's real state. The four stat cards keep their labels with a
// "—" placeholder; every feed renders its honest empty state. When
// `/admin/metrics` + the feed endpoints ship, swap this for the
// per-section TanStack queries (OVERVIEW_SPEC.md §8 independent
// loading). Tests drive populated layouts through `dataOverride`.

const EMPTY_OVERVIEW: AdminOverviewData = {
  staffOnline: 0,
  importsRunning: 0,
  stats: [
    { label: 'Active ISAs', value: '—', caption: '' },
    { label: 'Active MSAs', value: '—', caption: '' },
    { label: 'In testing', value: '—', caption: '' },
    { label: 'Closed (90d)', value: '—', caption: '' },
  ],
  recentCompanies: [],
  importsInQueue: 0,
  activeImports: [],
  activity: [],
};

function useAdminOverview(): AdminOverviewData {
  return React.useMemo(() => EMPTY_OVERVIEW, []);
}

// ── Page ───────────────────────────────────────────────────────

export interface AdminOverviewPageProps {
  /** Test seam: fixed "now" for the greeting + date. */
  readonly nowOverride?: Date;
  /** Test seam: override the sample data. */
  readonly dataOverride?: AdminOverviewData;
  /** matchMedia override for tests. */
  readonly compact?: boolean;
}

export function AdminOverviewPage(
  props: AdminOverviewPageProps = {},
): React.ReactElement {
  const me = useMe();
  const remote = useAdminOverview();
  const data = props.dataOverride ?? remote;
  const compact = useCompactMode(props.compact);
  const now = props.nowOverride ?? new Date();

  const greeting = greetingForHour(now.getHours());
  const firstName = greetingNameOf(me?.user.name, me?.user.email);
  const dateLabel = formatGreetingDate(now);

  return (
    <section
      aria-labelledby="admin-overview-heading"
      className="mx-auto w-full"
      style={{ maxWidth: '1080px', padding: compact ? '16px' : '32px' }}
    >
      {/* Eyebrow + greeting */}
      <p
        className="m-0 font-semibold"
        style={{
          fontSize: '12px',
          color: 'var(--fg-tertiary)',
          letterSpacing: '0.5px',
          textTransform: 'uppercase',
          marginBottom: '6px',
        }}
      >
        Internal admin
      </p>
      <h1
        id="admin-overview-heading"
        className="m-0 font-bold"
        style={{
          fontSize: compact ? '26px' : '30px',
          letterSpacing: '-0.5px',
          color: 'var(--fg-primary)',
          marginBottom: '6px',
        }}
      >
        {greeting}, {firstName}
      </h1>
      <p
        className="m-0"
        style={{
          fontSize: '14px',
          color: 'var(--fg-tertiary)',
          marginBottom: '24px',
        }}
      >
        {dateLabel}
        {data.staffOnline > 0 || data.importsRunning > 0
          ? ` · ${data.staffOnline} staff online · ${data.importsRunning} imports running`
          : ''}
      </p>

      {/* Stat cards */}
      <div
        className="grid"
        style={{
          gridTemplateColumns: compact
            ? 'repeat(2, minmax(0, 1fr))'
            : 'repeat(4, minmax(0, 1fr))',
          gap: '16px',
          marginBottom: '24px',
        }}
      >
        {data.stats.map((stat) => (
          <StatCard key={stat.label} stat={stat} compact={compact} />
        ))}
      </div>

      {/* Recent companies + Active imports */}
      <div
        className="grid"
        style={{
          gridTemplateColumns: compact
            ? '1fr'
            : 'minmax(0, 2fr) minmax(0, 1fr)',
          gap: '16px',
          marginBottom: '24px',
          alignItems: 'start',
        }}
      >
        <RecentCompaniesCard companies={data.recentCompanies} />
        <ActiveImportsCard
          imports={data.activeImports}
          inQueue={data.importsInQueue}
        />
      </div>

      {/* Activity feed */}
      <ActivityFeedCard activity={data.activity} />
    </section>
  );
}

// ── Stat card ──────────────────────────────────────────────────

function StatCard({
  stat,
  compact,
}: {
  readonly stat: OverviewStat;
  readonly compact: boolean;
}): React.ReactElement {
  return (
    <Card padding="none">
      <div style={{ padding: '20px' }}>
        <p
          className="m-0 font-semibold"
          style={{
            fontSize: '12px',
            color: 'var(--fg-tertiary)',
            letterSpacing: '0.5px',
            textTransform: 'uppercase',
            marginBottom: '10px',
          }}
        >
          {stat.label}
        </p>
        <p
          className="m-0 font-bold"
          style={{
            fontSize: compact ? '28px' : '32px',
            letterSpacing: '-0.5px',
            color: 'var(--fg-primary)',
            marginBottom: '8px',
            lineHeight: '1',
          }}
        >
          {stat.value}
        </p>
        <p
          className="m-0 truncate font-medium"
          style={{ fontSize: '13px', color: 'var(--fg-secondary)' }}
        >
          {stat.caption}
        </p>
      </div>
    </Card>
  );
}

// ── Card header (shared) ──────────────────────────────────────

function CardHeader({
  title,
  action,
}: {
  readonly title: string;
  readonly action?: React.ReactNode;
}): React.ReactElement {
  return (
    <header
      className="flex items-center justify-between"
      style={{
        padding: '16px 20px',
        borderBottom: '1px solid var(--border-default)',
        gap: '12px',
      }}
    >
      <h2
        className="m-0 font-semibold"
        style={{ fontSize: '15px', color: 'var(--fg-primary)' }}
      >
        {title}
      </h2>
      {action !== undefined ? action : null}
    </header>
  );
}

// ── Recent companies ───────────────────────────────────────────

function RecentCompaniesCard({
  companies,
}: {
  readonly companies: readonly OverviewCompany[];
}): React.ReactElement {
  return (
    <Card padding="none">
      <CardHeader
        title="Recent companies"
        action={
          <Button asChild variant="ghost" size="sm">
            <Link to="/admin/companies">All companies →</Link>
          </Button>
        }
      />
      {companies.length === 0 ? (
        <div style={{ padding: '20px' }}>
          <p
            className="m-0"
            style={{
              fontSize: '14px',
              color: 'var(--fg-secondary)',
              marginBottom: '12px',
            }}
          >
            No companies yet. Create your first one.
          </p>
          <Button asChild variant="primary" size="sm">
            <Link to="/admin/companies/new">+ New company</Link>
          </Button>
        </div>
      ) : (
        <ul className="m-0 flex list-none flex-col p-0">
          {companies.map((c, idx) => (
            <li
              key={c.id}
              data-testid={`overview-company-${c.id}`}
              className="flex items-center justify-between"
              style={{
                padding: '12px 20px',
                gap: '12px',
                borderTop:
                  idx === 0 ? 'none' : '1px solid var(--border-default)',
              }}
            >
              <div
                className="flex min-w-0 flex-1 items-center"
                style={{ gap: '10px' }}
              >
                <TypeChip type={c.type} size="sm" />
                <Link
                  to={`/admin/companies/${c.id}`}
                  className="truncate font-medium"
                  style={{ fontSize: '14px', color: 'var(--fg-primary)' }}
                >
                  {c.name}
                </Link>
              </div>
              <SystemStatusBadge status={c.status} showTooltip={false} />
              <span
                className="shrink-0 text-right"
                style={{
                  fontSize: '13px',
                  color: 'var(--fg-tertiary)',
                  width: '72px',
                }}
              >
                {c.region}
              </span>
              <span
                className="shrink-0 text-right font-medium"
                style={{
                  fontSize: '13px',
                  color: 'var(--fg-tertiary)',
                  width: '80px',
                }}
              >
                {c.propsCount === null ? '— props' : `${c.propsCount} props`}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

// ── Active imports ─────────────────────────────────────────────

function ActiveImportsCard({
  imports,
  inQueue,
}: {
  readonly imports: readonly OverviewImport[];
  readonly inQueue: number;
}): React.ReactElement {
  return (
    <Card padding="none">
      <header
        style={{
          padding: '16px 20px',
          borderBottom: '1px solid var(--border-default)',
        }}
      >
        <h2
          className="m-0 font-semibold"
          style={{ fontSize: '15px', color: 'var(--fg-primary)' }}
        >
          Active imports
        </h2>
        <p
          className="m-0"
          style={{
            fontSize: '12px',
            color: 'var(--fg-tertiary)',
            marginTop: '2px',
          }}
        >
          {inQueue} in queue
        </p>
      </header>
      {imports.length === 0 ? (
        <p
          className="m-0"
          style={{
            padding: '20px',
            fontSize: '13px',
            color: 'var(--fg-secondary)',
          }}
        >
          No imports in flight.
        </p>
      ) : (
        <ul className="m-0 flex list-none flex-col p-0">
          {imports.map((imp, idx) => {
            const pct =
              imp.rows_total <= 0
                ? 0
                : Math.round((imp.rows_processed / imp.rows_total) * 100);
            return (
              <li
                key={imp.id}
                data-testid={`overview-import-${imp.id}`}
                style={{
                  padding: '12px 20px',
                  borderTop:
                    idx === 0 ? 'none' : '1px solid var(--border-default)',
                }}
              >
                <div className="flex items-center" style={{ gap: '8px' }}>
                  <ImportStatusDot status={imp.status} />
                  <Link
                    to={`/admin/imports/${imp.id}`}
                    className="min-w-0 flex-1 truncate font-medium"
                    style={{ fontSize: '13px', color: 'var(--fg-primary)' }}
                  >
                    {imp.company_name}
                  </Link>
                </div>
                <p
                  className="m-0"
                  style={{
                    fontSize: '12px',
                    color: 'var(--fg-tertiary)',
                    marginTop: '4px',
                    marginLeft: '16px',
                  }}
                >
                  {importTypeLabel(imp.type).toLowerCase()} ·{' '}
                  {imp.rows_total.toLocaleString()} rows · {imp.when}
                </p>
                {imp.status === 'running' ? (
                  <div
                    role="progressbar"
                    aria-label={`Import progress: ${pct}%`}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={pct}
                    style={{
                      height: '3px',
                      borderRadius: 'var(--radius-full)',
                      background: 'var(--bg-sunken)',
                      overflow: 'hidden',
                      marginTop: '8px',
                      marginLeft: '16px',
                    }}
                  >
                    <div
                      aria-hidden="true"
                      style={{
                        width: `${pct}%`,
                        height: '100%',
                        background: 'var(--brand-500)',
                      }}
                    />
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
      <footer
        style={{
          padding: '12px 20px',
          borderTop: '1px solid var(--border-default)',
        }}
      >
        <Button asChild variant="ghost" size="sm">
          <Link to="/admin/imports">View all →</Link>
        </Button>
      </footer>
    </Card>
  );
}

// ── Activity feed ──────────────────────────────────────────────

function ActivityFeedCard({
  activity,
}: {
  readonly activity: readonly OverviewActivity[];
}): React.ReactElement {
  return (
    <Card padding="none">
      <CardHeader title="Activity feed" />
      {activity.length === 0 ? (
        <p
          className="m-0"
          style={{
            padding: '20px',
            fontSize: '13px',
            color: 'var(--fg-secondary)',
          }}
        >
          No recent activity.
        </p>
      ) : (
        <ul className="m-0 flex list-none flex-col p-0">
          {activity.slice(0, 10).map((a, idx) => (
            <li
              key={a.id}
              className="flex items-start justify-between"
              style={{
                padding: '12px 20px',
                gap: '12px',
                borderTop:
                  idx === 0 ? 'none' : '1px solid var(--border-default)',
              }}
            >
              <div className="min-w-0 flex-1">
                <p
                  className="m-0"
                  style={{ fontSize: '13px', color: 'var(--fg-primary)' }}
                >
                  <span className="font-semibold">{a.actor_name}</span>{' '}
                  {a.verb}
                </p>
                <p
                  className="m-0"
                  style={{
                    fontSize: '12px',
                    color: 'var(--fg-tertiary)',
                    marginTop: '2px',
                  }}
                >
                  {a.target}
                </p>
              </div>
              <span
                className="shrink-0"
                style={{
                  fontSize: '12px',
                  color: 'var(--fg-tertiary)',
                }}
              >
                {a.when}
              </span>
            </li>
          ))}
        </ul>
      )}
      <footer
        style={{
          padding: '12px 20px',
          borderTop: '1px solid var(--border-default)',
        }}
      >
        <p
          className="m-0"
          style={{ fontSize: '12px', color: 'var(--fg-tertiary)' }}
        >
          Per-company audit logs live in each company's Audit tab.
        </p>
      </footer>
    </Card>
  );
}
